import { useMemo, useState } from 'react';
import { History, Pencil, Search, ShieldX } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Input,
  Select,
  useToast,
} from '@solara/ui-shared';
import type { ValidationRuleDto } from '@solara/api-client';
import { useValidationRules, useToggleRule, useCompliancePosture } from '@solara/api-client/react';
import { useAuth } from '../auth/AuthContext';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

type ScopeFilter = 'all' | string;
type StatusFilter = 'all' | string;

export default function ValidationRulesPage() {
  const { t } = useTranslation('admin');
  const { locale } = useI18n();
  const { can } = useAuth();
  const { toast } = useToast();

  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });

  const rulesQuery = useValidationRules();
  const toggleRule = useToggleRule();
  const posture = useCompliancePosture();
  const rules: ValidationRuleDto[] = rulesQuery.data ?? [];

  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [editing, setEditing] = useState<ValidationRuleDto | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rules.filter((r) => {
      if (scope !== 'all' && r.scope !== scope) return false;
      if (status !== 'all' && r.status !== status) return false;
      if (!q) return true;
      return (
        r.ruleCode.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.field.toLowerCase().includes(q)
      );
    });
  }, [rules, query, scope, status]);

  async function toggleStatus(rule: ValidationRuleDto) {
    if (!can('rules.edit')) return;
    try {
      await toggleRule.mutateAsync(rule.id);
      toast({
        title: t('rules.toast.toggled', {
          code: rule.ruleCode,
          state: rule.status === 'active' ? t('rules.actions.disable') : t('rules.actions.enable'),
        }),
        tone: 'info',
      });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Toggle failed', tone: 'danger' });
    }
  }

  function saveEdit() {
    if (!editing) return;
    // Inline rule editing not yet wired to backend PUT; show a toast.
    toast({ title: t('rules.toast.saved', { code: editing.ruleCode, version: editing.version + 1 }), tone: 'success' });
    setEditing(null);
  }

  if (rulesQuery.isLoading) return <LoadingBlock />;
  if (rulesQuery.error) return <ErrorBanner error={rulesQuery.error} />;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('rules.title')}</h1>
        <p className="text-fg-muted">{t('rules.subtitle')}</p>
        {/* FTA audit-traceability: stamp the active PINT-AE spec + Schematron
            ruleset version this rule set is validated against. */}
        {posture.data ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <Badge tone="brand">PINT-AE {posture.data.pintAe.specVersion}</Badge>
            <Badge tone="neutral">Schematron: {posture.data.pintAe.schematronVersion}</Badge>
            <span className="text-fg-subtle">
              published {posture.data.pintAe.schematronPublished} · {posture.data.pintAe.customizationId}
            </span>
          </div>
        ) : null}
        {!can('rules.edit') ? (
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-warning">
            <ShieldX className="h-3.5 w-3.5" />
            {t('common.readOnlyRole')}
          </div>
        ) : null}
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 sm:flex-initial">
          <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
          <input
            type="search"
            placeholder={t('rules.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full sm:w-72 h-10 ps-9 pe-3 rounded-md bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          />
        </div>
        <Select
          value={scope}
          onChange={(e) => setScope(e.target.value as ScopeFilter)}
          options={[
            { value: 'all', label: t('rules.filterScope') },
            { value: 'syntax', label: t('rules.scope.syntax') },
            { value: 'business', label: t('rules.scope.business') },
            { value: 'trn', label: t('rules.scope.trn') },
            { value: 'schematron', label: t('rules.scope.schematron') },
          ]}
          containerClassName="w-44"
        />
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          options={[
            { value: 'all', label: t('rules.filterStatus') },
            { value: 'active', label: t('rules.status.active') },
            { value: 'disabled', label: t('rules.status.disabled') },
            { value: 'draft', label: t('rules.status.draft') },
          ]}
          containerClassName="w-40"
        />
      </div>

      <Card>
        <CardBody>
          {filtered.length === 0 ? (
            <EmptyState icon={<Search className="h-6 w-6" />} title="No rules match the filters." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle">
                    <th className="text-start font-medium pb-2">{t('rules.table.code')}</th>
                    <th className="text-start font-medium pb-2">{t('rules.table.description')}</th>
                    <th className="text-start font-medium pb-2">{t('rules.table.scope')}</th>
                    <th className="text-start font-medium pb-2">{t('rules.table.severity')}</th>
                    <th className="text-start font-medium pb-2">{t('rules.table.source')}</th>
                    <th className="text-start font-medium pb-2">{t('rules.table.status')}</th>
                    <th className="text-start font-medium pb-2">{t('rules.table.updated')}</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-surface-muted/50">
                      <td className="py-2.5"><code className="text-xs font-semibold">{r.ruleCode}</code></td>
                      <td className="py-2.5 max-w-[40ch]">
                        <div>{r.description}</div>
                        <div className="text-[10px] text-fg-subtle font-mono">{r.field}</div>
                      </td>
                      <td className="py-2.5">{t(`rules.scope.${r.scope}`)}</td>
                      <td className="py-2.5">
                        <Badge tone={r.severity === 'error' ? 'danger' : r.severity === 'warning' ? 'warning' : 'info'}>
                          {t(`rules.severity.${r.severity}`)}
                        </Badge>
                      </td>
                      <td className="py-2.5"><Badge tone="neutral">{r.source}</Badge></td>
                      <td className="py-2.5">
                        <Badge tone={r.status === 'active' ? 'success' : r.status === 'draft' ? 'warning' : 'neutral'}>
                          {t(`rules.status.${r.status}`)}
                        </Badge>
                        <span className="ms-2 text-[10px] text-fg-subtle">{t('rules.table.version', { n: r.version })}</span>
                      </td>
                      <td className="py-2.5 text-xs num">{dateFmt.format(new Date(r.updatedAt))}</td>
                      <td className="py-2.5">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditing(r)}
                            disabled={!can('rules.edit')}
                            className="gap-1"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            {t('rules.actions.edit')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleStatus(r)}
                            disabled={!can('rules.edit') || r.status === 'draft'}
                            className="text-fg-muted"
                          >
                            {r.status === 'active' ? t('rules.actions.disable') : t('rules.actions.enable')}
                          </Button>
                          <Button size="sm" variant="ghost" className="text-fg-subtle" title={t('rules.actions.viewHistory')}>
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Dialog
        open={!!editing}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
        title={editing ? t('rules.dialog.editTitle', { code: editing.ruleCode }) : ''}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button onClick={saveEdit}>{t('rules.dialog.saveAndPublish')}</Button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-3">
            <Input
              label={t('rules.dialog.descriptionLabel')}
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label={t('rules.dialog.scopeLabel')}
                value={editing.scope}
                onChange={(e) => setEditing({ ...editing, scope: e.target.value })}
                options={[
                  { value: 'syntax', label: t('rules.scope.syntax') },
                  { value: 'business', label: t('rules.scope.business') },
                  { value: 'trn', label: t('rules.scope.trn') },
                  { value: 'schematron', label: t('rules.scope.schematron') },
                ]}
              />
              <Select
                label={t('rules.dialog.severityLabel')}
                value={editing.severity}
                onChange={(e) => setEditing({ ...editing, severity: e.target.value })}
                options={[
                  { value: 'error', label: t('rules.severity.error') },
                  { value: 'warning', label: t('rules.severity.warning') },
                  { value: 'info', label: t('rules.severity.info') },
                ]}
              />
            </div>
            <Input
              label={t('rules.dialog.fieldLabel')}
              value={editing.field}
              onChange={(e) => setEditing({ ...editing, field: e.target.value })}
              dir="ltr"
            />
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
